/**
 * Atomic route create orchestration with rollback on partial failure.
 */
import {
  logRouteCreateRollbackDebug,
  logRouteCreateStageDebug,
  validateExecutionTopologyBeforeRouteCreate,
  type RouteCreateExecutionValidationResult
} from './executionTopologyHelpers';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import type { FromStopDraftByUid, QuantityDraftByCargo, ToStopDraftByUid } from './executionDraftTypes';
import type { MainStopFormFields } from './routeStopsSavePipeline';
import type { ImmutableExecutionSnapshot } from './immutableExecutionSnapshot';
import { validateStableExecutionSnapshot } from './executionTopologyStabilization';
import { toCompatibilityExecutionSnapshot } from './immutableExecutionSnapshot';
import { logSavePipelineDebug } from './executionDebugLogging';
import { store } from '../../store';
import { apiSlice } from '../../store/api/apiSlice';
import { routeDeleteReadModelInvalidatesTags } from '../../store/api/routeDeleteReadModelTags';

export type RouteCreateAllocationRow = {
  cargoId: number;
  quantity: number;
  fromStopId?: number;
  toStopId?: number;
};

/** Assigned only after stops + allocations succeed — prevents ghost busy driver. */
export type DeferredRouteResourceAssignment = {
  driverId?: number;
  vehicleId?: number;
  trailerVehicleId?: number;
  carrierId?: number;
};

export type RouteCreatePipelineParams = {
  routeRequest: Record<string, unknown>;
  payloadStops: Record<string, unknown>[];
  allocationLinks?: Array<{ fromLoadingPosition: number; toUnloadingPosition: number }>;
  allocations: RouteCreateAllocationRow[];
  executionSnapshot?: ImmutableExecutionSnapshot;
  deferredResourceAssignment?: DeferredRouteResourceAssignment;
  mapAllocationsToStopIds: (
    allocations: RouteCreateAllocationRow[],
    savedStops: Array<{ id: number; position: number }>
  ) => RouteCreateAllocationRow[];
  validateExecution?: () => RouteCreateExecutionValidationResult;
  createRoute: (body: Record<string, unknown>) => { unwrap: () => Promise<{ id: number; number?: string }> };
  startExecutionSession?: (args: {
    executionSnapshot?: Record<string, unknown>;
    deferredAssignment?: DeferredRouteResourceAssignment;
  }) => { unwrap: () => Promise<{ sessionId: string }> };
  rollbackExecutionSession?: (args: {
    sessionId: string;
    reason?: string;
  }) => { unwrap: () => Promise<unknown> };
  upsertRouteStops: (args: {
    routeId: number;
    stops: Record<string, unknown>[];
    allocationLinks?: Array<{ fromLoadingPosition: number; toUnloadingPosition: number }>;
    executionSnapshot?: Record<string, unknown>;
    executionSessionId?: string;
  }) => { unwrap: () => Promise<Array<{ id: number; position: number; clientExecutionUid?: string; stopUid?: string }>> };
  createAllocations: (args: {
    routeId: number;
    allocations: RouteCreateAllocationRow[];
    executionSnapshot?: Record<string, unknown>;
    finalizeRouteCreate?: boolean;
    executionSessionId?: string;
  }) => { unwrap: () => Promise<unknown> };
  deleteRoute: (routeId: number) => { unwrap: () => Promise<void> };
  assignRouteResources?: (args: {
    routeId: number;
    assignment: DeferredRouteResourceAssignment;
  }) => { unwrap: () => Promise<unknown> };
  /** After partial create failure — sync RTK read models (defaults to deleteRoute tag set). */
  syncReadModelsAfterPartialFailure?: (routeId: number) => void | Promise<void>;
};

export type RouteCreatePipelineResult = {
  routeId: number;
  routeNumber?: string;
  savedStops: Array<{ id: number; position: number }>;
};

export class RouteCreatePipelineError extends Error {
  constructor(
    message: string,
    public readonly rolledBack: boolean,
    public readonly routeId?: number
  ) {
    super(message);
    this.name = 'RouteCreatePipelineError';
  }
}

function stripResourceAssignmentFromCreateRequest(
  body: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...body };
  delete next.driverId;
  delete next.vehicleId;
  delete next.trailerVehicleId;
  return next;
}

export async function syncRouteCreateFailureReadModels(
  routeId: number,
  override?: (routeId: number) => void | Promise<void>
): Promise<void> {
  const sync =
    override ??
    ((id: number) => {
      store.dispatch(apiSlice.util.invalidateTags(routeDeleteReadModelInvalidatesTags(id)));
    });
  await sync(routeId);
}

function assertExecutionSnapshotReady(snapshot?: ImmutableExecutionSnapshot): void {
  if (!snapshot) return;
  const validation = validateStableExecutionSnapshot(snapshot);
  if (!validation.ok) {
    throw new RouteCreatePipelineError(
      validation.message ?? 'Execution topology inconsistent',
      false
    );
  }
  for (const alloc of snapshot.allocations) {
    if (!alloc.fromStopUid || !alloc.toStopUid || alloc.quantity <= 0) {
      throw new RouteCreatePipelineError(
        'Маршрут не может быть создан: неполный execution graph',
        false
      );
    }
  }
}

export async function executeRouteCreatePipeline(
  params: RouteCreatePipelineParams
): Promise<RouteCreatePipelineResult> {
  const validation = params.validateExecution?.();
  if (validation && !validation.ok) {
    throw new RouteCreatePipelineError(validation.message ?? 'Маршрут не может быть создан', false);
  }

  assertExecutionSnapshotReady(params.executionSnapshot);

  let routeId: number | null = null;
  let rolledBack = false;

  const createBody =
    params.deferredResourceAssignment != null
      ? { ...stripResourceAssignmentFromCreateRequest(params.routeRequest), deferSideEffects: Boolean(params.executionSnapshot) }
      : params.executionSnapshot
        ? { ...params.routeRequest, deferSideEffects: true }
        : params.routeRequest;

  const compatSnapshotBody = params.executionSnapshot
    ? toCompatibilityExecutionSnapshot(params.executionSnapshot)
    : undefined;

  let executionSessionId: string | undefined;
  if (params.executionSnapshot && params.startExecutionSession) {
    const session = await params
      .startExecutionSession({
        executionSnapshot: compatSnapshotBody,
        deferredAssignment: params.deferredResourceAssignment
      })
      .unwrap();
    executionSessionId = session.sessionId;
    logSavePipelineDebug('execution_session_start', { sessionId: executionSessionId });
  }

  try {
    logRouteCreateStageDebug('validate_ok');
    logSavePipelineDebug('route_create', {
      deferredAssignment: Boolean(params.deferredResourceAssignment),
      stopCount: params.payloadStops.length,
      allocCount: params.allocations.length
    });

    const created = await params
      .createRoute({
        ...createBody,
        ...(executionSessionId ? { executionSessionId } : {})
      })
      .unwrap();
    routeId = Number(created.id);
    logRouteCreateStageDebug('route_created', { routeId });

    const savedStops = await params
      .upsertRouteStops({
        routeId,
        stops: params.payloadStops,
        allocationLinks: params.allocationLinks,
        executionSnapshot: compatSnapshotBody,
        executionSessionId
      })
      .unwrap();
    logRouteCreateStageDebug('stops_saved', { count: savedStops.length });
    logSavePipelineDebug('stop_save', { routeId, count: savedStops.length });

    if (params.allocations.length > 0) {
      const withStopIds = params.mapAllocationsToStopIds(params.allocations, savedStops);
      const missingStopLink = withStopIds.some(
        (row) => row.fromStopId == null || row.toStopId == null
      );
      if (missingStopLink) {
        throw new Error(
          'Не удалось сохранить маршрут: обнаружено частичное распределение грузов'
        );
      }
      await params
        .createAllocations({
          routeId,
          allocations: withStopIds,
          executionSnapshot: compatSnapshotBody,
          finalizeRouteCreate: Boolean(params.executionSnapshot),
          executionSessionId
        })
        .unwrap();
      logRouteCreateStageDebug('allocations_saved', { count: withStopIds.length });
      logSavePipelineDebug('allocation_save', { routeId, count: withStopIds.length });
    } else {
      logRouteCreateStageDebug('allocations_skipped');
    }

    if (params.deferredResourceAssignment && params.assignRouteResources) {
      await params
        .assignRouteResources({ routeId, assignment: params.deferredResourceAssignment })
        .unwrap();
      logSavePipelineDebug('deferred_assignment', { routeId, ...params.deferredResourceAssignment });
    }

    return {
      routeId,
      routeNumber: created.number,
      savedStops
    };
  } catch (err) {
    let deleteRollbackFailed: RouteCreatePipelineError | undefined;

    if (executionSessionId && params.rollbackExecutionSession) {
      try {
        await params
          .rollbackExecutionSession({
            sessionId: executionSessionId,
            reason: err instanceof Error ? err.message : 'Create pipeline failed'
          })
          .unwrap();
        rolledBack = true;
        logRouteCreateStageDebug('orchestration_rollback_ok', { sessionId: executionSessionId });
      } catch (rollbackErr) {
        logRouteCreateRollbackDebug(routeId ?? 0, rollbackErr);
      }
    } else if (routeId != null) {
      try {
        await params.deleteRoute(routeId).unwrap();
        rolledBack = true;
        logRouteCreateRollbackDebug(routeId, err);
        logRouteCreateStageDebug('rollback_ok', { routeId });
        logSavePipelineDebug('rollback', { routeId, reason: err });
      } catch (rollbackErr) {
        logRouteCreateRollbackDebug(routeId, rollbackErr);
        logRouteCreateStageDebug('rollback_failed', { routeId, rollbackErr });
        deleteRollbackFailed = new RouteCreatePipelineError(
          'Не удалось сохранить маршрут: частично созданный маршрут не удалось откатить. Обратитесь к администратору.',
          false,
          routeId
        );
      }
    }

    if (routeId != null) {
      try {
        await syncRouteCreateFailureReadModels(routeId, params.syncReadModelsAfterPartialFailure);
        logRouteCreateStageDebug('read_models_sync', { routeId });
      } catch (syncErr) {
        logRouteCreateRollbackDebug(routeId, syncErr);
      }
    }

    if (deleteRollbackFailed) {
      throw deleteRollbackFailed;
    }

    const message =
      err instanceof RouteCreatePipelineError
        ? err.message
        : extractRouteCreateErrorMessage(err);

    throw new RouteCreatePipelineError(message, rolledBack, routeId ?? undefined);
  }
}

export function buildRouteCreateExecutionValidator(params: {
  groupUiEnabled: boolean;
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: import('./executionDraftTypes').AllocationEdgeDraft[];
  cargoApplicationById?: Record<number, number | undefined>;
  cargoAvailableById?: Record<number, number | undefined>;
  cargoCapById?: Record<number, number | undefined>;
}): () => RouteCreateExecutionValidationResult {
  return () => {
    if (!params.groupUiEnabled || params.groups.length === 0) {
      return { ok: true };
    }
    return validateExecutionTopologyBeforeRouteCreate({
      groups: params.groups,
      mainLoading: params.mainLoading,
      mainUnloading: params.mainUnloading,
      selectedIds: params.selectedIds,
      fromStopDraft: params.fromStopDraft,
      toStopDraft: params.toStopDraft,
      quantityDraft: params.quantityDraft,
      allocationEdges: params.allocationEdges,
      cargoApplicationById: params.cargoApplicationById,
      cargoAvailableById: params.cargoAvailableById,
      cargoCapById: params.cargoCapById
    });
  };
}

function extractRouteCreateErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const data = (err as { data?: { error?: string; data?: { code?: string; failingPosition?: number } } }).data;
    if (data?.error) {
      const detail = data.data?.failingPosition != null
        ? ` (позиция ${data.data.failingPosition})`
        : data.data?.code
          ? ` [${data.data.code}]`
          : '';
      return `${data.error}${detail}`;
    }
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  return 'Не удалось сохранить маршрут';
}
